import { InfoRow, SectionTitle } from "./PropertyRegridSnapshotElements";

const ROUGHNESS_DESCRIPTIONS = {
  0: "Level",
  1: "Very gentle slopes",
  2: "Gentle",
  3: "Moderate",
  4: "Steep",
  5: "Very steep",
  6: "Extremely rugged",
};

const GROWTH_THRESHOLD = 0.5;

function formatCurrency(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value)))
    return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercentage(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value)))
    return null;
  return `${Number(value).toFixed(2)}%`;
}

function categorizeGrowth(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "unknown";
  }
  const numeric = Number(value);
  if (numeric >= GROWTH_THRESHOLD) return "up";
  if (numeric <= -GROWTH_THRESHOLD) return "down";
  return "stable";
}

function describeGrowthState(direction, tense) {
  if (direction === "unknown") return "sem dados suficientes";
  if (direction === "up") return tense === "past" ? "cresceu" : "deve continuar crescendo";
  if (direction === "down") return tense === "past" ? "encolheu" : "deve continuar em queda";
  return tense === "past" ? "esteve estável" : "deve permanecer estável";
}

function describeRoughness(value) {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  const description = ROUGHNESS_DESCRIPTIONS[numeric] || "Rugosidade desconhecida";
  return `${numeric} — ${description}`;
}

function describeHousingAffordability(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "Dados indisponíveis (HAI).";
  }
  const numeric = Number(value);
  if (numeric > 100) {
    return "HAI > 100 indica que moradores médios conseguem comprar a casa mediana sem sobrecarga.";
  }
  if (numeric < 100) {
    return "HAI < 100 indica casas menos acessíveis e renda pode não cobrir imóvel mediano sem custos excessivos.";
  }
  return "HAI ~ 100, acessibilidade próxima da média.";
}

function describeVacancy(code, date) {
  if (!code || code === "N") {
    return date ? `Verificado em ${date}` : "Sem reporte de vacância";
  }
  return date ? `Indicou vacância em ${date}` : "Indicou vacância";
}

function describeGrowthDifference(past, next) {
  if (!Number.isFinite(past) || !Number.isFinite(next)) {
    return null;
  }
  const diff = next - past;
  let insight = "Projeções muito próximas.";
  if (next > 0) {
    insight =
      diff >= 0
        ? "Projeção sugere aceleração do crescimento."
        : "Crescimento ainda positivo, mas desacelerando.";
  } else if (next < 0) {
    insight =
      diff >= 0
        ? "Declínio projetado perdendo força."
        : "Declínio projetado em aceleração.";
  } else {
    insight = "Projeta estabilidade.";
  }
  return { diff, insight };
}

function formatDifference(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const prefix = value >= 0 ? "+" : "";
  return `${prefix}${value.toFixed(2)}%`;
}

export function IdentificationSection({ snapshot }) {
  return (
    <div>
      <SectionTitle title="Identificação" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <InfoRow label="Parcel" value={snapshot.parcel_number} />
        <InfoRow label="Owner" value={snapshot.owner_name} />
        <InfoRow label="Deed owner" value={snapshot.deed_owner_name} />
        <InfoRow
          label="Mailing"
          value={snapshot.mailing_address || snapshot.original_mailing_address}
        />
        <InfoRow
          label="Parcel unformatted"
          value={snapshot.parcel_number_unformatted}
        />
      </div>
    </div>
  );
}

export function LocationSection({ snapshot }) {
  const qozLabel =
    snapshot.qoz && snapshot.qoz !== "No"
      ? "Qualified Opportunity Zone (sim)"
      : "Qualified Opportunity Zone";

  return (
    <div>
      <SectionTitle title="Localização & zoneamento" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <InfoRow label="Cidade" value={snapshot.city} />
        <InfoRow label="Condado" value={snapshot.county} />
        <InfoRow label="Estado" value={snapshot.state_code} />
        <InfoRow label="CEP" value={snapshot.zip_code || snapshot.zip5} />
        <InfoRow label="Zoning" value={snapshot.zoning_code} />
        <InfoRow label="Zoning Type" value={snapshot.zoning_type} />
        <InfoRow label="Zoning Subtype" value={snapshot.zoning_subtype} />
        <InfoRow
          label="Escola unificada"
          value={snapshot.census_unified_school_district}
        />
        <InfoRow label={qozLabel} value={snapshot.qoz} />
      </div>
    </div>
  );
}

export function ConstructionSection({ snapshot }) {
  return (
    <div>
      <SectionTitle title="Construção & infraestrutura" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <InfoRow label="Rooms" value={snapshot.num_rooms} />
        <InfoRow label="Bedrooms" value={snapshot.num_bedrooms} />
        <InfoRow
          label="Bathrooms"
          value={snapshot.num_bath}
          caption={`Parciais: ${snapshot.num_bath_partial ?? "—"}`}
        />
        <InfoRow label="Stories" value={snapshot.num_stories} />
        <InfoRow label="Area (sqft)" value={snapshot.area_building} />
        <InfoRow label="Area definition" value={snapshot.area_building_definition} />
        <InfoRow label="Building count" value={snapshot.building_count} />
      </div>
    </div>
  );
}

export function MarketSection({ snapshot }) {
  const pastDirection = categorizeGrowth(snapshot.housing_growth_past_5_years);
  const nextDirection = categorizeGrowth(snapshot.housing_growth_next_5_years);
  const growthSummary = `Nos últimos 5 anos ${describeGrowthState(
    pastDirection,
    "past",
  )} e para os próximos 5 anos ${describeGrowthState(nextDirection, "future")}.`;
  const growthCaption =
    !Number.isNaN(Number(snapshot.housing_growth_past_5_years)) &&
    !Number.isNaN(Number(snapshot.housing_growth_next_5_years))
      ? `Variação estimada: ${(
          Number(snapshot.housing_growth_next_5_years) -
          Number(snapshot.housing_growth_past_5_years)
        ).toFixed(2)}%`
      : null;
  const roughnessDescription = describeRoughness(snapshot.roughness_rating);
  const growthPastValue = Number.isFinite(
    Number(snapshot.housing_growth_past_5_years ?? null),
  )
    ? Number(snapshot.housing_growth_past_5_years)
    : null;
  const growthNextValue = Number.isFinite(
    Number(snapshot.housing_growth_next_5_years ?? null),
  )
    ? Number(snapshot.housing_growth_next_5_years)
    : null;
  const diffInfo = describeGrowthDifference(growthPastValue, growthNextValue);
  const diffLabel = diffInfo ? formatDifference(diffInfo.diff) : "—";

  return (
    <div>
      <SectionTitle title="Mercado & demografia" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <InfoRow
          label="Median Income"
          value={formatCurrency(snapshot.median_household_income)}
        />
        <InfoRow
          label="Population density (per mi²)"
          value={snapshot.population_density}
        />
        <InfoRow
          label="Housing affordability (HAI)"
          value={snapshot.housing_affordability_index}
          caption={describeHousingAffordability(snapshot.housing_affordability_index)}
        />
        <InfoRow
          label="Roughness (terrain)"
          value={snapshot.roughness_rating}
          caption={roughnessDescription}
        />
        <InfoRow
          label="InSite Score"
          value={snapshot.insite_score}
          caption="NatureServe InSite Score, valor integrado de biodiversidade"
        />
      </div>
      <div className="mt-3 border border-gray-100 rounded-lg bg-slate-50 p-3">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-gray-500">
          <span>Housing growth</span>
          <span className="text-[10px]">{growthCaption ?? "—"}</span>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <div className="min-w-[110px]">
            <div className="text-[10px] text-gray-500">Last 5y</div>
            <div className="text-sm font-semibold text-gray-900">
              {formatPercentage(snapshot.housing_growth_past_5_years) ?? "—"}
            </div>
            <div className="text-[10px] text-gray-400">
              {describeGrowthState(pastDirection, "past")}
            </div>
          </div>
          <div className="min-w-[110px]">
            <div className="text-[10px] text-gray-500">Next 5y</div>
            <div className="text-sm font-semibold text-gray-900">
              {formatPercentage(snapshot.housing_growth_next_5_years) ?? "—"}
            </div>
            <div className="text-[10px] text-gray-400">
              {describeGrowthState(nextDirection, "future")}
            </div>
          </div>
          <div className="flex-1 min-w-[150px] rounded-md border border-dashed border-gray-200 p-3">
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">
              Variação estimada
            </div>
            <div className="text-sm font-semibold text-gray-900">{diffLabel}</div>
            <div className="text-[10px] text-gray-400">
              {diffInfo?.insight ??
                "Comparação entre crescimento passado e projeção atual."}
            </div>
          </div>
        </div>
        <div className="text-[11px] text-gray-500 mt-2">{growthSummary}</div>
      </div>
    </div>
  );
}

export function RiskSection({ snapshot }) {
  const transmissionDistance = snapshot.transmission_line_distance;
  const isClose =
    transmissionDistance !== null &&
    transmissionDistance !== undefined &&
    Number(transmissionDistance) < 500;
  const riskHint = isClose
    ? "Linha de transmissão próxima (menos de 500 m)"
    : "Linha de transmissão distante ou sem dados";
  const vacancyInfo = describeVacancy(
    snapshot.usps_vacancy,
    snapshot.usps_vacancy_date,
  );

  return (
    <div>
      <SectionTitle title="Risco & utilidades" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <InfoRow
          label="Transmission line"
          value={
            transmissionDistance
              ? `${Number(transmissionDistance).toFixed(2)} m`
              : null
          }
          caption={riskHint}
        />
        <InfoRow
          label="USPS vacancy"
          value={snapshot.usps_vacancy === "Y" ? "Indicou vacância" : "Sem reporte"}
          caption={vacancyInfo}
        />
      </div>
    </div>
  );
}

export function LbcsSection({ snapshot }) {
  return (
    <div>
      <SectionTitle title="LBCS (descrições)" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <InfoRow label="Activity" value={snapshot.lbcs_activity_desc} />
        <InfoRow label="Function" value={snapshot.lbcs_function_desc} />
        <InfoRow label="Structure" value={snapshot.lbcs_structure_desc} />
        <InfoRow label="Site / Ownership" value={snapshot.lbcs_ownership_desc} />
      </div>
    </div>
  );
}
